import React, { useEffect, useMemo, useState } from "react";
import { Modal, Radio, Checkbox, Switch, Button, message, Divider, Tag } from "antd";
import authApi from "../../../api/authApi";
import {
    PERMISSION_GROUPS,
    FLAG_PERMISSIONS,
    CTV_BASIC_PRESET,
    normalizePerms,
} from "../../../config/admin-permissions";

const FLAG_KEYS = FLAG_PERMISSIONS.map((f) => f.key);

/**
 * Modal set vai trò (user <-> mod) + tick quyền chi tiết cho 1 user (CTV).
 * user: { id, name, email, role, permissions } | null
 */
function UserPermissionModal({ user, onClose, onSuccess }) {
    const [role, setRole] = useState("user");
    const [perms, setPerms] = useState({}); // { key: true }
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!user) return;
        setRole(user.role === "mod" ? "mod" : "user");
        setPerms(normalizePerms(user.permissions));
    }, [user]);

    const isAdmin = user?.role === "admin";

    const toggle = (key, val) =>
        setPerms((p) => {
            const next = { ...p };
            if (val) next[key] = true;
            else delete next[key];
            return next;
        });

    const toggleGroup = (items, val) =>
        setPerms((p) => {
            const next = { ...p };
            items.forEach(({ key }) => {
                if (val) next[key] = true;
                else delete next[key];
            });
            return next;
        });

    const applyPreset = () => {
        setRole("mod");
        setPerms({ ...CTV_BASIC_PRESET });
    };

    const enabledCount = useMemo(
        () => Object.keys(perms).filter((k) => !FLAG_KEYS.includes(k) && perms[k]).length,
        [perms]
    );

    const handleSave = async () => {
        setSaving(true);
        try {
            // role=user -> gửi permissions rỗng (backend xoá).
            const payload = role === "mod" ? perms : {};
            await authApi.updateUserPermissions(user.id, { role, permissions: payload });
            message.success("Đã cập nhật phân quyền");
            onSuccess?.();
        } catch (e) {
            message.error(e?.response?.data?.message || "Cập nhật thất bại");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={!!user}
            title={
                <div>
                    <span>Phân quyền CTV</span>
                    {user && (
                        <div className="text-xs text-content-3 font-normal mt-1">
                            #{user.id} — {user.name} · {user.email}
                        </div>
                    )}
                </div>
            }
            onCancel={onClose}
            width={640}
            footer={[
                <Button key="preset" onClick={applyPreset} disabled={isAdmin}>
                    Preset CTV cơ bản
                </Button>,
                <Button key="cancel" onClick={onClose}>
                    Hủy
                </Button>,
                <Button key="save" type="primary" loading={saving} onClick={handleSave} disabled={isAdmin}>
                    Lưu
                </Button>,
            ]}
        >
            {isAdmin ? (
                <div className="py-4 text-center text-orange-500">
                    Tài khoản này là <b>admin</b> — không thể chỉnh quyền tại đây.
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    <div>
                        <div className="font-semibold mb-1">Vai trò</div>
                        <Radio.Group value={role} onChange={(e) => setRole(e.target.value)}>
                            <Radio value="user">User (thường)</Radio>
                            <Radio value="mod">
                                Mod / CTV{" "}
                                {enabledCount > 0 && <Tag color="blue">{enabledCount} quyền</Tag>}
                            </Radio>
                        </Radio.Group>
                    </div>

                    {role === "mod" && (
                        <>
                            <Divider className="my-1" />
                            {/* Quyền đặc biệt */}
                            <div className="flex flex-col gap-2 bg-blue-50 rounded-md p-3">
                                {FLAG_PERMISSIONS.map((f) => (
                                    <div key={f.key} className="flex items-center justify-between">
                                        <span className="text-sm">{f.label}</span>
                                        <Switch
                                            checked={!!perms[f.key]}
                                            onChange={(v) => toggle(f.key, v)}
                                        />
                                    </div>
                                ))}
                            </div>

                            {/* Quyền theo mục */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {PERMISSION_GROUPS.map((group) => {
                                    const allOn = group.items.every((it) => perms[it.key]);
                                    return (
                                        <div key={group.title} className="border rounded-md p-2">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="font-semibold text-sm">{group.title}</span>
                                                <Checkbox
                                                    checked={allOn}
                                                    onChange={(e) => toggleGroup(group.items, e.target.checked)}
                                                >
                                                    <span className="text-xs text-content-3">Tất cả</span>
                                                </Checkbox>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                {group.items.map((it) => (
                                                    <Checkbox
                                                        key={it.key}
                                                        checked={!!perms[it.key]}
                                                        onChange={(e) => toggle(it.key, e.target.checked)}
                                                    >
                                                        <span className="text-sm">{it.label}</span>
                                                    </Checkbox>
                                                ))}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            )}
        </Modal>
    );
}

export default UserPermissionModal;
